import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { IonIcon, IonSpinner } from "@ionic/react";
import {
  personOutline,
  shieldCheckmarkOutline,
  helpCircleOutline,
  informationCircleOutline,
  cardOutline,
  constructOutline,
  chevronForwardOutline,
  arrowBackOutline,
  logOutOutline,
  sparklesOutline,
  lockClosedOutline,
} from "ionicons/icons";
import { useAuth } from "../../contexts/AuthContext";
import { db, signOutUser } from "../../lib/firebase/firebase";

import PrivacySettingsPage from "./PrivacySettingsPage";
import AccountSettingsPage from "./AccountSettingsPage";
import HelpSupportPage from "./HelpSupportPage";
import AboutPage from "./AboutPage";
import SubscriptionSettingsPage from "./SubscriptionSettingsPage";
import SupportOpsPage from "./SupportOpsPage";
import "./SettingsPage.css";

const SettingsPage = ({ onClose }) => {
  const { user, userIsPremium } = useAuth();
  const [currentView, setCurrentView] = useState("main");
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [hasSupportOpsAccess, setHasSupportOpsAccess] = useState(null);

  const email = (user?.email || "").toLowerCase();
  const hasCompatibilityAccess = [
    "admin@svgtrace.com",
    "moderator@svgtrace.com",
  ].includes(email);

  useEffect(() => {
    let active = true;

    const loadAdminAccess = async () => {
      if (!user?.uid) {
        if (active) setHasSupportOpsAccess(false);
        return;
      }

      setHasSupportOpsAccess(null);
      try {
        const adminSnapshot = await getDoc(doc(db, "adminUsers", user.uid));
        const isActiveAdmin =
          adminSnapshot.exists() && adminSnapshot.data()?.isActive === true;
        if (active) {
          setHasSupportOpsAccess(isActiveAdmin || hasCompatibilityAccess);
        }
      } catch (error) {
        console.error("Failed to read Support Ops authorization:", error);
        if (active) setHasSupportOpsAccess(hasCompatibilityAccess);
      }
    };

    loadAdminAccess();
    return () => {
      active = false;
    };
  }, [hasCompatibilityAccess, user?.uid]);

  useEffect(() => {
    if (currentView === "supportOps" && hasSupportOpsAccess === false) {
      setCurrentView("main");
    }
  }, [currentView, hasSupportOpsAccess]);

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOutUser();
      if (onClose) onClose();
    } catch (error) {
      console.error("Failed to sign out:", error);
    } finally {
      setSignOutLoading(false);
    }
  };

  const handleBack = () => setCurrentView("main");

  if (currentView === "account") {
    return <AccountSettingsPage onBack={handleBack} />;
  }
  if (currentView === "privacy") {
    return <PrivacySettingsPage onBack={handleBack} />;
  }
  if (currentView === "help") {
    return <HelpSupportPage onBack={handleBack} />;
  }
  if (currentView === "about") {
    return <AboutPage onBack={handleBack} />;
  }
  if (currentView === "subscription") {
    return <SubscriptionSettingsPage onBack={handleBack} />;
  }
  if (currentView === "supportOps" && hasSupportOpsAccess === true) {
    return <SupportOpsPage onBack={handleBack} />;
  }
  if (currentView === "supportOps" && hasSupportOpsAccess === null) {
    return (
      <div className="settings-page">
        <div className="sop-loading">
          <IonSpinner name="crescent" />
          <p>Checking admin access…</p>
        </div>
      </div>
    );
  }

  const isPro = !!userIsPremium;
  const displayName = user?.displayName || user?.email?.split("@")[0] || "User";
  const initial = (user?.email || "U").charAt(0).toUpperCase();

  const menuItems = [
    {
      icon: personOutline,
      iconBg: "#eff6ff",
      iconColor: "#3b82f6",
      title: "Account Settings",
      subtitle: user?.email || "Manage your account",
      action: () => setCurrentView("account"),
    },
    {
      icon: cardOutline,
      iconBg: "#fdf4ff",
      iconColor: "#a855f7",
      title: "Subscription",
      subtitle: isPro ? "Manage your plan" : "Upgrade to Pro",
      action: () => setCurrentView("subscription"),
    },
    {
      icon: shieldCheckmarkOutline,
      iconBg: "#ecfdf5",
      iconColor: "#10b981",
      title: "Privacy & Security",
      subtitle: "EULA, privacy policy, data preferences",
      action: () => setCurrentView("privacy"),
    },
    {
      icon: helpCircleOutline,
      iconBg: "#fff7ed",
      iconColor: "#f97316",
      title: "Help & Support",
      subtitle: "FAQs and contact support",
      action: () => setCurrentView("help"),
    },
    {
      icon: informationCircleOutline,
      iconBg: "#f1f5f9",
      iconColor: "#64748b",
      title: "About SvgTrace",
      subtitle: "App version and information",
      action: () => setCurrentView("about"),
    },
  ];

  if (hasSupportOpsAccess === true) {
    menuItems.splice(3, 0, {
      icon: constructOutline,
      iconBg: "#eef2ff",
      iconColor: "#4f46e5",
      title: "Support Ops",
      subtitle: "Review tickets, pending confirms, and actions",
      action: () => setCurrentView("supportOps"),
    });
  }

  return (
    <div className="settings-page">
      <div className="sp-header">
        <button className="sp-back-btn" onClick={onClose}>
          <IonIcon icon={arrowBackOutline} />
        </button>
        <h2 className="sp-header-title">Settings</h2>
      </div>

      <div className="sp-scroll">
        {/* Profile Card */}
        <div className="sp-section">
          <div className="sp-profile">
            <div className="sp-avatar">{initial}</div>
            <div className="sp-profile-info">
              <p className="sp-profile-name">{displayName}</p>
              <p className="sp-profile-email">{user?.email || "No email"}</p>
              <span
                className={`sp-badge ${isPro ? "sp-badge--pro" : "sp-badge--free"}`}
              >
                <IonIcon
                  icon={isPro ? sparklesOutline : lockClosedOutline}
                  style={{ fontSize: 12 }}
                />
                {isPro ? "Pro" : "Free"}
              </span>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="sp-section">
          {menuItems.map((item, i) => (
            <div key={i} className="sp-item" onClick={item.action}>
              <div
                className="sp-item-icon"
                style={{ background: item.iconBg, color: item.iconColor }}
              >
                <IonIcon icon={item.icon} />
              </div>
              <div className="sp-item-body">
                <p className="sp-item-title">{item.title}</p>
                <p className="sp-item-sub">{item.subtitle}</p>
              </div>
              <IonIcon
                icon={chevronForwardOutline}
                className="sp-item-chevron"
              />
            </div>
          ))}
        </div>

        {/* Sign Out */}
        <div className="sp-section">
          <div
            className="sp-signout"
            onClick={!signOutLoading ? handleSignOut : undefined}
          >
            {signOutLoading ? (
              <IonSpinner name="lines" style={{ width: 22, height: 22 }} />
            ) : (
              <IonIcon icon={logOutOutline} className="sp-signout-icon" />
            )}
            <span className="sp-signout-label">
              {signOutLoading ? "Signing out..." : "Sign Out"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
